import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';

import {
  UPDATE_CUSTOM_GRAPH,
  REMOVE_CUSTOM_GRAPH
} from '../../../../../../../graphql/Survey';

import MCBar from './components/Bar/MCBar';
import RatingBar from './components/Bar/RatingBar';
import FeaturesList from '../FeaturesList/FeaturesList';
import GraphActionsToolbar from '../Toolbars/GraphActionsToolbar';
import GraphTitle from '../GraphTitle/GraphTitle';
import GraphInfoTabs from '../GraphInfoTabs/GraphInfoTabs';
import Disclaimer from '../Disclaimer/Disclaimer';
import LoversHaters from '../LoversHaters/LoversHaters';
import Branding from '../Branding/Branding';
import Popup from '../../../../../Popup';
import EditBackgroundPopup from '../EditBackgroundPopup/EditBackgroundPopup';

import calculateExtremeUsersData from '../../helpers/calculateExtremeUsersData';
import generateRandomColor from '../../helpers/generateRandomColor';
import calculateChartData from '../../helpers/calculateChartData';
import updateGraphFeatures from '../../helpers/updateGraphFeatures';
import getAllowedQuestions from '../../helpers/getAllowedQuestions';

import DutchStrings from '../../../../../../../../users/strings/nl';
import FrenchStrings from '../../../../../../../../users/strings/fr';
import EnglishStrings from '../../../../../../../../users/strings/en';

import { templateConfigurations, templateTypes } from '../../helpers/constants';

import styles from '../MergedGraph/MergedGraph.module.css';

const graphInfoTabs = {
  LEGEND: 'Select data points',
  LOVERS_HATERS: 'Lovers/haters',
  DISCLAIMER: 'Help'
};

const questionTypes = {
  xQuestion: templateConfigurations[templateTypes.CUSTOM_GRAPH].xQuestionName,
  yQuestion: templateConfigurations[templateTypes.CUSTOM_GRAPH].yQuestionName
};

const loadedStrings = {
  Dutch: DutchStrings,
  French: FrenchStrings,
  English: EnglishStrings
};

export default ({
  graph,
  questions,
  distributorGroups,
  surveyId,
  surveyLanguage,
  setEditGraphsPopup,
  customGraphIndex,
  isFiltered,
  activeFilters,
  defaultDataPointColors,
  setDisplayContactInnovationExpertPopup,
  isAuthenticated,
  onToggleFilter,
  onSetFiltersRelation,
  viewToken
}) => {
  const [updateCustomGraph] = useMutation(UPDATE_CUSTOM_GRAPH);

  const [activegraphInfoTab, setActiveGraphInfoTab] = useState(
    graphInfoTabs.LEGEND
  );
  const [activeLoversHaters, setActiveLoversHaters] = useState();
  const [
    selectedExtremeUsersFeature,
    setSelectedExtremeUsersFeature
  ] = useState();
  const [
    selectedExtremeUsersFilter,
    setSelectedExtremeUsersFilter
  ] = useState();
  const [
    previousActiveLoversHaters,
    setPreviousActiveLoversHaters
  ] = useState();
  const [dataPointColors, setDataPointColors] = useState(
    defaultDataPointColors
  );
  const [themeColors, setThemeColors] = useState([
    {
      name: 'COLOR_1',
      colorCode: '#FFFFFF'
    },
    {
      name: 'COLOR_2',
      colorCode: '#FFFFFF'
    },
    {
      name: 'COLOR_3',
      colorCode: '#FFFFFF'
    },
    {
      name: 'COLOR_4',
      colorCode: '#FFFFFF'
    }
  ]);
  const [editBackgroundPopup, setEditBackgroundPopup] = useState(false);
  const [features, setFeatures] = useState([]);
  const [unFilteredFeatures, setUnFilteredFeatures] = useState([]);
  const [filteringOnSelection, setFilteringOnSelection] = useState(true);
  const [presentationModeEnabled, setPresentationModeEnabled] = useState(false);

  const barParent = useRef();

  const [
    removeCustomGraph,
    { loading: removeCustomGraphLoading }
  ] = useMutation(REMOVE_CUSTOM_GRAPH);

  const onRemoveCustomGraphClick = async () => {
    if (!removeCustomGraphLoading) {
      await removeCustomGraph({
        variables: {
          survey: surveyId,
          id: graph.id,
          ...(viewToken ? { viewToken } : {})
        }
      });

      if (activeFilters && activeFilters.length) {
        if (graph && graph.name) {
          const newFilters = activeFilters.find(
            f => f.filterName === graph.name
          );
          if (newFilters) {
            onToggleFilter(
              newFilters.filterName,
              newFilters.valuePath,
              newFilters.value,
              newFilters.resultsPath
            );
          }
        }
      }
    }
  };

  const translations =
    surveyLanguage && loadedStrings[surveyLanguage]
      ? loadedStrings[surveyLanguage]
      : loadedStrings.English;

  const getExtremeUsersData = () => {
    const extremeUsersResultIds = calculateExtremeUsersData(
      activeLoversHaters,
      reducedQuestions,
      questionTypes
    );

    if (!activeFilters.length) {
      onSetFiltersRelation('or');
    } else if (
      previousActiveLoversHaters &&
      (activeFilters.length &&
        activeFilters.find(
          filter =>
            filter.value === previousActiveLoversHaters &&
            filter.filterName === graph.name
        ))
    ) {
      // Remove existing filter when selecting different one in dropdown
      onToggleFilter(graph.name, null, previousActiveLoversHaters, null, false);
    }
    setPreviousActiveLoversHaters(activeLoversHaters.filter);
    onToggleFilter(
      graph.name,
      null,
      activeLoversHaters.filter,
      null,
      result => {
        if (
          result &&
          result.id &&
          extremeUsersResultIds.indexOf(result.id) > -1
        ) {
          return true;
        }
        return false;
      }
    );
  };

  const getChartData = useUnfilteredResultStats => {
    if (
      features &&
      features.length &&
      reducedQuestions &&
      reducedQuestions.length
    ) {
      return calculateChartData(
        features,
        unFilteredFeatures,
        useUnfilteredResultStats,
        questionTypes,
        reducedQuestions,
        filteringOnSelection,
        { graphRange: false }
      );
    }
    return [];
  };

  const reducedQuestions = useMemo(() => getAllowedQuestions(questions), [
    questions
  ]);

  const data = useMemo(() => getChartData(false), [
    questions,
    JSON.stringify(features),
    filteringOnSelection,
    activeFilters.length
  ]);
  const unFilteredData = useMemo(() => getChartData(true), [
    JSON.stringify(unFilteredFeatures),
    filteringOnSelection,
    activeFilters.length
  ]);

  useEffect(
    () => {
      if (
        activeLoversHaters &&
        activeLoversHaters.filter &&
        activeLoversHaters.xQuestion &&
        activeLoversHaters.yQuestion
      ) {
        getExtremeUsersData();
      }
    },
    [activeLoversHaters, graph.features.length]
  );

  useEffect(
    () => {
      // On removal of filter in FiltersBar component
      let needToUpdate = false;
      if (activeLoversHaters && activeLoversHaters.filter) {
        if (
          !activeFilters.length ||
          (activeFilters.length &&
            activeFilters.findIndex(
              filter =>
                filter.value === activeLoversHaters.filter &&
                filter.filterName === graph.name
            ) === -1)
        ) {
          needToUpdate = true;
        }
      }
      if (needToUpdate) {
        setActiveLoversHaters(null);
      }
    },
    [activeFilters]
  );

  useEffect(
    () => {
      // Replace default data point color with custom defined
      if (features) {
        let dirty = false;
        const newDataPointColors = [...dataPointColors];
        features.forEach((fG, i) => {
          if (fG.colorCode) {
            dirty = true;
            newDataPointColors[i] = fG.colorCode;
          }
        });

        if (dirty) {
          setDataPointColors(newDataPointColors);
        }
      }

      if (data && data.length && data.length > dataPointColors.length) {
        const replenishedColors = [...dataPointColors];
        const dataLength = data.length;
        const defaultDataPointColorsLength = dataPointColors.length;
        for (let i = 0; i < dataLength - defaultDataPointColorsLength; i += 1) {
          replenishedColors.push(generateRandomColor());
        }
        setDataPointColors(replenishedColors);
      }
    },
    [data]
  );

  useEffect(
    () => {
      if (graph && graph.customThemeColors && graph.customThemeColors.length) {
        setThemeColors(graph.customThemeColors);
      }
      if (
        graph &&
        graph.features &&
        graph.features.length &&
        !features.length
      ) {
        setFeatures(graph.features);
      }
    },
    [graph]
  );

  useEffect(
    () => {
      if (features.length && isFiltered && !unFilteredFeatures.length) {
        // Set unfiltered features when activating filter
        setUnFilteredFeatures([...features].map(f => ({ ...f })));
      } else if (unFilteredFeatures.length && !isFiltered) {
        setUnFilteredFeatures([]);
      }
    },
    [isFiltered, features]
  );

  const xQuestion = reducedQuestions.find(
    q => q.id === graph.features[0].xQuestion
  );
  const yQuestion = reducedQuestions.find(
    q => q.id === graph.features[0].yQuestion
  );

  const axisEdgeLabels = {
    xLeft: '',
    xRight: '',
    yTop: '',
    yBottom: ''
  };

  const scaleQuestionMinMax = {
    xQuestionMin: 0,
    xQuestionMax: 0,
    yQuestionMin: 0,
    yQuestionMax: 0
  };

  if (xQuestion && xQuestion.type === 'Slider') {
    const allXQuestions = graph.features.map(feature =>
      reducedQuestions.find(q => q.id === feature.xQuestion)
    );
    const allYQuestions = graph.features.map(feature =>
      reducedQuestions.find(q => q.id === feature.yQuestion)
    );

    scaleQuestionMinMax.xQuestionMin = allXQuestions.reduce(
      (acc, q) => (q.minValue < acc ? q.minValue : acc),
      Infinity
    );
    scaleQuestionMinMax.xQuestionMax = allXQuestions.reduce(
      (acc, q) => (q.maxValue > acc ? q.maxValue : acc),
      -Infinity
    );

    scaleQuestionMinMax.yQuestionMin = allYQuestions.reduce(
      (acc, q) => (q.minValue < acc ? q.minValue : acc),
      Infinity
    );
    scaleQuestionMinMax.yQuestionMax = allYQuestions.reduce(
      (acc, q) => (q.maxValue > acc ? q.maxValue : acc),
      -Infinity
    );
  }

  const matrixAnswerOptions = [
    { answer: translations.MATRIX.I_STRONGLY_DISAGREE },
    { answer: translations.MATRIX.I_DISAGREE },
    { answer: translations.MATRIX.I_AGREE },
    { answer: translations.MATRIX.I_STRONGLY_AGREE }
  ];

  if (
    xQuestion &&
    (xQuestion.type === 'Multiple Choice Question' ||
      xQuestion.type === 'Matrix')
  ) {
    let xQuestionAnswers = [];
    if (xQuestion.type === 'Matrix') {
      xQuestionAnswers = matrixAnswerOptions;
    } else if (xQuestion.answers && xQuestion.answers.length) {
      xQuestionAnswers = [...xQuestion.answers];
    }

    if (xQuestionAnswers && xQuestionAnswers[0]) {
      axisEdgeLabels.xLeft = xQuestionAnswers[0].answer;
    }
    if (
      xQuestionAnswers &&
      xQuestionAnswers.length &&
      xQuestionAnswers[xQuestionAnswers.length - 1]
    ) {
      axisEdgeLabels.xRight =
        xQuestionAnswers[xQuestionAnswers.length - 1].answer;
    }

    let yQuestionAnswers = [];
    if (yQuestion.type === 'Matrix') {
      yQuestionAnswers = matrixAnswerOptions;
    } else if (yQuestion.answers && yQuestion.answers.length) {
      yQuestionAnswers = [...yQuestion.answers];
    }
    if (yQuestionAnswers && yQuestionAnswers[0]) {
      axisEdgeLabels.yTop =
        yQuestionAnswers[yQuestionAnswers.length - 1].answer;
    }
    if (
      yQuestionAnswers &&
      yQuestionAnswers.length &&
      yQuestionAnswers[yQuestionAnswers.length - 1]
    ) {
      axisEdgeLabels.yBottom = yQuestionAnswers[0].answer;
    }
  }

  return (
    <div className={`${styles.container} custom-graph-container`}>
      <GraphActionsToolbar
        onEdit={() => {
          setEditGraphsPopup({
            ...graph,
            features: [...features],
            index: customGraphIndex,
            type: templateTypes.CUSTOM_GRAPH
          });
        }}
        onRemove={onRemoveCustomGraphClick}
        filteringOnSelection={filteringOnSelection}
        setFilteringOnSelection={setFilteringOnSelection}
        isFiltered={isFiltered}
        setEditBackgroundPopup={setEditBackgroundPopup}
        presentationModeEnabled={presentationModeEnabled}
        setPresentationModeEnabled={setPresentationModeEnabled}
      />
      <GraphTitle
        graphName={(graph && graph.name) || ''}
        data={data}
        questionsHaveResults={
          reducedQuestions &&
          reducedQuestions.length &&
          reducedQuestions.some(
            rQ => rQ.unfilteredResults && rQ.unfilteredResults.length
          )
        }
      />
      <GraphInfoTabs
        graphInfoTabs={graphInfoTabs}
        activegraphInfoTab={activegraphInfoTab}
        setActiveGraphInfoTab={setActiveGraphInfoTab}
      />
      <div
        className={`${styles.blockContent} ${styles.opportunityGraphContent}`}
      >
        {features && features.length ? (
          <>
            <div className={styles.opportunityChartContainer} ref={barParent}>
              {xQuestion &&
              (xQuestion.type === 'Multiple Choice Question' ||
                xQuestion.type === 'Matrix') ? (
                <MCBar
                  parent={barParent}
                  width="460"
                  height="430"
                  data={data}
                  unFilteredData={unFilteredData}
                  isFiltered={!!activeFilters.length}
                  numberOfXAnswers={
                    xQuestion.answers ? xQuestion.answers.length - 1 : 0
                  }
                  numberOfYAnswers={
                    yQuestion.answers ? yQuestion.answers.length - 1 : 0
                  }
                  axisEdgeLabels={axisEdgeLabels}
                  xLabel={graph.xLabel}
                  yLabel={graph.yLabel}
                  dataPointColors={dataPointColors.filter(
                    (_dataPoint, index) =>
                      features[index] && features[index].active
                  )}
                  unFilteredDataPointColors={dataPointColors.filter(
                    (_dataPoint, index) =>
                      unFilteredFeatures[index] &&
                      unFilteredFeatures[index].active
                  )}
                  questions={questions}
                  themeColors={themeColors}
                />
              ) : null}
              {xQuestion && xQuestion.type === 'Slider' ? (
                <RatingBar
                  parent={barParent}
                  width="400"
                  height="400"
                  data={data}
                  unFilteredData={unFilteredData}
                  isFiltered={!!activeFilters.length}
                  scaleQuestionMinMax={scaleQuestionMinMax}
                  axisEdgeLabels={axisEdgeLabels}
                  xLabel={graph.xLabel}
                  yLabel={graph.yLabel}
                  dataPointColors={dataPointColors.filter(
                    (_dataPoint, index) =>
                      features[index] && features[index].active
                  )}
                  unFilteredDataPointColors={dataPointColors.filter(
                    (_dataPoint, index) =>
                      unFilteredFeatures[index] &&
                      unFilteredFeatures[index].active
                  )}
                  questions={questions}
                  themeColors={themeColors}
                />
              ) : null}
            </div>
            <div className={styles.graphInfoContainer}>
              {activegraphInfoTab === graphInfoTabs.LEGEND && (
                <FeaturesList
                  features={features}
                  setFeatures={setFeatures}
                  dataPointColors={dataPointColors}
                  setDataPointColors={setDataPointColors}
                  saveUpdatedFeatures={updatedFeatures => {
                    if (isAuthenticated || viewToken) {
                      updateGraphFeatures(
                        graph.id,
                        surveyId,
                        updatedFeatures,
                        updateCustomGraph,
                        viewToken || null
                      );
                    }
                  }}
                  questions={reducedQuestions}
                  questionTypes={questionTypes}
                  distributorGroups={distributorGroups}
                  isFiltered={!!activeFilters.length}
                  unFilteredFeatures={unFilteredFeatures}
                  setUnFilteredFeatures={setUnFilteredFeatures}
                  filteringOnSelection={filteringOnSelection}
                  activeFiltersLength={activeFilters && activeFilters.length}
                  templateType={templateTypes.CUSTOM_GRAPH}
                  presentationModeEnabled={presentationModeEnabled}
                />
              )}
              {activegraphInfoTab === graphInfoTabs.LOVERS_HATERS && (
                <LoversHaters
                  activeLoversHaters={activeLoversHaters}
                  setActiveLoversHaters={setActiveLoversHaters}
                  features={features}
                  dataPointColors={dataPointColors}
                  selectedFeature={selectedExtremeUsersFeature}
                  setSelectedFeature={setSelectedExtremeUsersFeature}
                  selectedFilter={selectedExtremeUsersFilter}
                  setSelectedFilter={setSelectedExtremeUsersFilter}
                  explanation="When trying to figure out which ideas to address, it can be helpful to focus on
                  respondents who are most enthusiastic about a particular idea and will thus be the promotors.
                  What other ideas do they find important and what characteristics does this group have?"
                />
              )}
              {activegraphInfoTab === graphInfoTabs.BRANDING && (
                <Branding
                  updateGraphMutation={updateCustomGraph}
                  graphId={graph.id}
                  features={features}
                  setFeatures={setFeatures}
                  surveyId={surveyId}
                  defaultDataPointColors={defaultDataPointColors}
                  themeColors={themeColors}
                  setThemeColors={setThemeColors}
                  dataPointColors={dataPointColors}
                  setDataPointColors={setDataPointColors}
                  selectedExtremeUsersFeature={selectedExtremeUsersFeature}
                  setSelectedExtremeUsersFeature={
                    setSelectedExtremeUsersFeature
                  }
                  questionTypes={questionTypes}
                  isAuthenticated={isAuthenticated}
                  viewToken={viewToken}
                />
              )}
              {activegraphInfoTab === graphInfoTabs.DISCLAIMER && (
                <Disclaimer
                  setDisplayContactInnovationExpertPopup={
                    setDisplayContactInnovationExpertPopup
                  }
                />
              )}
            </div>
          </>
        ) : (
          <div className={`${styles.noData} no-chart-data`}>No data</div>
        )}
      </div>
      {editBackgroundPopup && (
        <Popup
          component={
            <EditBackgroundPopup
              onClose={() => setEditBackgroundPopup(false)}
              templateType={templateTypes.CUSTOM_GRAPH}
              templateTypes={templateTypes}
              themeColors={themeColors}
              onSaveGraph={newThemeColors => {
                setThemeColors(newThemeColors);
                if (isAuthenticated || viewToken) {
                  updateCustomGraph({
                    variables: {
                      id: graph.id,
                      survey: surveyId,
                      customThemeColors: newThemeColors,
                      ...(viewToken ? { viewToken } : {})
                    }
                  });
                }
              }}
              questions={questions}
              xQuestion={xQuestion}
              yQuestion={yQuestion}
              axisEdgeLabels={axisEdgeLabels}
              xLabel={graph.xLabel}
              yLabel={graph.yLabel}
            />
          }
          customStyles={{ padding: 'none', overflow: 'visible' }}
          onClose={() => setEditBackgroundPopup(false)}
        />
      )}
    </div>
  );
};
